import { Delete, UploadFile } from '@mui/icons-material';
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Typography,
} from '@mui/material';
import React, { useCallback, useRef } from 'react';
import { useDropzone } from 'react-dropzone';
import { useSelector } from 'react-redux';
import { DocumentCard } from '../DocumentPortal/DocumentSortingDialog/DocumentCard';
import { fileToBase64 } from '../Document/DocumentOverview';
import { IDocument } from '../Interfaces/IDocument';
import { IMIMEType } from '../Interfaces/IMIMEType';
import { State } from '../redux/mainReducer';

interface IProps {
  documentArray: IDocument[];
  setDocumentArray: (documents: IDocument[]) => void;
  /** Geschlossene Aufgabe -> Upload/Remove ausblenden (read-only). */
  disableEdit?: boolean;
  /** Lazy-Load im Vorgang laeuft noch -> Ladeindikator statt Karten. */
  isLoading?: boolean;
}

/**
 * Dokumentbereich einer Aufgabe: Karten-Vorschau (DocumentCard) mit Upload und
 * Entfernen. Arbeitet rein auf dem uebergebenen documentArray/setDocumentArray;
 * die Persistenz uebernimmt der jeweilige Aufgaben-Flow (Standalone-Speichern
 * bzw. Vorgang-Speichern). Neue Dateien bekommen eine negative temporaere
 * idDocument + Base64 in Data (identisch zu DocumentOverview).
 */
export const TaskDocuments: React.FC<IProps> = ({
  documentArray,
  setDocumentArray,
  disableEdit = false,
  isLoading = false,
}) => {
  const mimeTypeArray = useSelector(
    (state: State) => state.mimeTypeArray.storeAcademicTitle
  );
  const inputUpload = useRef<HTMLInputElement | null>(null);

  const handleFilesUpload = useCallback(
    async (acceptedFiles: File[]) => {
      let minIdOffset = -1;

      if (documentArray.length > 0) {
        const minIdArray = Math.min(
          ...documentArray.map(document => document.idDocument)
        );
        if (minIdArray <= minIdOffset) {
          minIdOffset = minIdArray - 1;
        }
      }

      const uploadArray: IDocument[] = [];
      let index = 0;

      for (const currentFile of acceptedFiles) {
        const fileName: string = String(currentFile['name']);
        const mimeType: IMIMEType | undefined = mimeTypeArray.find(
          mime => mime.MIMEType === currentFile['type']
        );

        if (mimeType) {
          uploadArray.push({
            idDocument: minIdOffset - index,
            FileName: fileName,
            idMIMEType: mimeType.idMIMEType,
            Data: await fileToBase64(currentFile),
            Created_at: new Date().toISOString(),
          } as IDocument);
        }
        index = index + 1;
      }

      setDocumentArray([...documentArray, ...uploadArray]);
    },
    [documentArray, mimeTypeArray, setDocumentArray]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: handleFilesUpload,
    noClick: true,
    disabled: disableEdit,
  });

  const handleRemove = (idDocument: number) => {
    setDocumentArray(
      documentArray.filter(document => document.idDocument !== idDocument)
    );
  };

  const acceptString = mimeTypeArray.map(mime => mime.MIMEType).join(',');

  return (
    <Box sx={{ mt: 2 }} {...getRootProps()}>
      <input {...getInputProps()} />
      <input
        ref={inputUpload}
        type='file'
        multiple
        hidden
        accept={acceptString}
        onChange={e => {
          if (e.target.files) {
            handleFilesUpload(Array.from(e.target.files));
          }
          e.target.value = '';
        }}
      />

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 1,
        }}
      >
        <Typography variant='h6'>Dokumente</Typography>
        {!disableEdit && (
          <Button
            size='small'
            startIcon={<UploadFile />}
            onClick={() => inputUpload.current?.click()}
          >
            Dokument hinzufügen
          </Button>
        )}
      </Box>

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
          <CircularProgress size={24} />
        </Box>
      ) : documentArray.length === 0 ? (
        <Typography variant='body2' color='text.secondary'>
          Keine Dokumente
        </Typography>
      ) : (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
          {documentArray.map((document, index) => (
            <Box
              key={document.idDocument ?? index}
              sx={{ position: 'relative', width: 200 }}
            >
              <DocumentCard initialDocument={document} selectable={false} />
              {!disableEdit && (
                <IconButton
                  size='small'
                  onClick={() => handleRemove(document.idDocument)}
                  sx={{
                    position: 'absolute',
                    top: 4,
                    right: 4,
                    backgroundColor: 'rgba(255,255,255,0.8)',
                    '&:hover': { backgroundColor: 'rgba(255,255,255,0.95)' },
                  }}
                >
                  <Delete fontSize='small' />
                </IconButton>
              )}
            </Box>
          ))}
        </Box>
      )}

      {isDragActive && !disableEdit && (
        <Typography variant='body2' color='primary' sx={{ mt: 1 }}>
          Dateien hier ablegen …
        </Typography>
      )}
    </Box>
  );
};
